import { createMap } from '@shared/utils';

import type { QBSLevelItemExcludingTag } from '../types';

import { updateQBankStructureLevelItemsOrder } from './order';

export function cascadeDeleteQBankStructureLevelItemsById(
  qBankStructureLevelId: string,
  qBankStructureLevelItems: QBSLevelItemExcludingTag[],
) {
  const deletedQBankStructureLevelIds: Set<string> = new Set();

  const queue: string[] = [qBankStructureLevelId];
  deletedQBankStructureLevelIds.add(qBankStructureLevelId);

  while (queue.length > 0) {
    const currentLevelId = queue.shift();
    // Find direct children of the current Id and add their Ids to the queue
    const children = qBankStructureLevelItems.filter(
      (qBankStructureLevelItem) => qBankStructureLevelItem.parentLevelItemId === currentLevelId,
    );

    children.forEach((child) => {
      deletedQBankStructureLevelIds.add(child.id);
      queue.push(child.id);
    });
  }

  // Filter out the items that need to be deleted
  return qBankStructureLevelItems.filter(
    (qBankStructureLevelItem) => !deletedQBankStructureLevelIds.has(qBankStructureLevelItem.id),
  );
}

export function deleteQBankStructureLevelItemsById(
  qBankStructureLevelId: string,
  qBankStructureLevelItems: QBSLevelItemExcludingTag[],
) {
  const qBankStructureLevelItemToDelete = qBankStructureLevelItems.find(
    (qBankStructureLevelItem) => qBankStructureLevelItem.id === qBankStructureLevelId,
  );

  const isRootLevel = qBankStructureLevelItemToDelete?.parentLevelItemId === null;

  // Return if level item not found or trying to delete root level
  if (!qBankStructureLevelItemToDelete || isRootLevel) return qBankStructureLevelItems;

  // Step 1: Delete level items with the given Id and its children
  const remainingQBankStructureLevelItems = cascadeDeleteQBankStructureLevelItemsById(
    qBankStructureLevelId,
    qBankStructureLevelItems,
  );

  // Step 2: Reorder remaining level items in the same level
  // Find level items which has same parent and in the same level
  const qBankStructureLevelItemsNeedToReorder = remainingQBankStructureLevelItems.filter(
    (qBankStructureLevelItem) =>
      // Level items which has same parent and in the same level
      qBankStructureLevelItem.parentLevelItemId ===
        qBankStructureLevelItemToDelete.parentLevelItemId &&
      qBankStructureLevelItem.qBankStructureLevelId ===
        qBankStructureLevelItemToDelete.qBankStructureLevelId,
  );

  const reorderedQBankStructureLevelItems = updateQBankStructureLevelItemsOrder(
    qBankStructureLevelItemsNeedToReorder,
  );
  const reorderedQBankStructureLevelItemMap = createMap<QBSLevelItemExcludingTag, 'id'>(
    reorderedQBankStructureLevelItems,
    'id',
  );

  const updatedRemainingLevelItems = remainingQBankStructureLevelItems.map(
    (qBankStructureLevelItem) =>
      reorderedQBankStructureLevelItemMap.get(qBankStructureLevelItem.id) ??
      qBankStructureLevelItem,
  );

  return updatedRemainingLevelItems;
}
